/**
 * 订单状态的中文标签与颜色分组 —— **全仓唯一一份**。
 *
 * 搬的是 apps/admin-ops/src/views/OrdersView.vue 里那张表。搬的理由不是整洁:
 * 两端各写一份之后,加一个新状态时改了运营端、忘了监管端,页面上会原样显示
 * `agency_done` 这样的英文串 —— 而**它看起来就像一个状态**,没人会当成 bug 报上来。
 *
 * 标签里的括号注解(「已创建(不产出)」)是原文,一个字都没删:
 * 它们交代的是「这个状态会停留吗」,演示时被问到就得答得上来。
 *
 * 状态取值本身来自 packages/shared 的 ORDER_STATUS(单点定义),这里只负责
 * 「中文叫什么」与「归哪一组颜色」—— 不重新声明有哪些状态。
 */

/** 四组颜色。`unknown` 不是一组,它复用 `draft`(灰) */
export type StatusGroup = 'draft' | 'active' | 'attention' | 'done'

export const ORDER_STATUS_LABELS: Readonly<Record<string, string>> = {
  created: '已创建(不产出)',
  pending_payment: '待支付',
  paid: '已付款(不停留)',
  agency_processing: '代办·办理中',
  agency_done: '代办·已办完',
  pending_accept: '等接单(无人抢)',
  accepted: '已成交',
  picked_up: '已取货',
  in_transit: '运输中',
  delivered: '已送达',
  drone_returned: '无人机返回',
  completed: '已完成',
  cancelled: '已取消',
  refunding: '退款中',
  refunded: '已退款',
}

export const ORDER_STATUS_GROUPS: Readonly<Record<string, StatusGroup>> = {
  created: 'draft',
  /**
   * 待支付 = **橙**(attention;2026-10-16,S-42 按画板 O1 改)。
   *
   * 它原来落 draft(灰)。改这一格的依据是 v2.1 的 G4 已经裁过的语义
   * (「待支付 / 待取货 = 橙」)—— 那一轮只改了小程序那一侧(它有一张覆盖表),
   * 两个后台端的基础表没跟着动,于是同一个状态在两端是两种颜色:
   * 客户那边是橙的「要你动一下」,运营这边是灰的「没什么事」。
   * 运营端的订单驾驶舱(O1)画板上「待支付」这一格画的正是橙 —— 一次端内自相矛盾。
   *
   * 改在基础表上而不是再开一张运营端覆盖表:覆盖表是给「两端口径本来就不一样」的
   * 那几处用的(小程序的 accepted),而这里两端要的是同一档语义;
   * 运营端再抄一张覆盖表,下一次改色就要改三处。连带:小程序那张覆盖表里
   * pending_payment 那一行随之撤掉(它从此与基础表同值,留着是一句会骗人的注释)。
   */
  pending_payment: 'attention',
  paid: 'draft',
  cancelled: 'draft',
  refunded: 'draft',

  agency_processing: 'active',
  accepted: 'active',
  picked_up: 'active',
  in_transit: 'active',
  drone_returned: 'active',

  pending_accept: 'attention',
  agency_done: 'attention',
  refunding: 'attention',

  delivered: 'done',
  completed: 'done',
}

/**
 * 标签:查不到就原样返回那个字符串。
 *
 * 这不是兜底 —— 这是**故意让它难看**。服务端加了一个新状态而前端还没跟上时,
 * 显示 `agency_done` 比显示「未知」有用得多:它一眼就能看出是漏了一处映射,
 * 而「未知」会被读成「数据有问题」,排查方向一开始就是错的。
 */
export function statusLabel(status: string): string {
  return ORDER_STATUS_LABELS[status] ?? status
}

/** 分组:查不到按 `draft`(灰)渲染。灰色配上那句原样字符串,就是「这一条没人认领」的视觉 */
export function statusGroup(status: string): StatusGroup {
  return ORDER_STATUS_GROUPS[status] ?? 'draft'
}
